import { useState, useEffect } from 'react'
import { ethers } from 'ethers'

// AGC: PlayerXP contract ABI (only functions we need)
// AGC: FIX 2025-11-28 - Corrected ABI to match actual contract (experienceToNextLevel instead of lastBattleTimestamp)
const PLAYER_XP_ABI = [
  'function getPlayerData(address player) external view returns (uint256 totalExperience, uint8 level, uint256 experienceToNextLevel, uint32 totalBattles, uint32 wins, uint32 losses, uint32 draws)'
]

export interface BlockchainStats {
  totalExperience: number
  level: number
  lastBattleTimestamp: number
  totalBattles: number
  wins: number
  losses: number
  draws: number
  winRate: number
  lossRate: number
  drawRate: number
}

/**
 * AGC: Hook to fetch player battle statistics from the blockchain
 * AGC: FIX 2025-11-28 - Now reads DIRECTLY from blockchain first (source of truth)
 * AGC: Falls back to backend API only if blockchain query fails
 */
export const useBlockchainStats = (playerAddress: string) => {
  const [stats, setStats] = useState<BlockchainStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let isUnmounted = false

    // AGC: FIX 2025-11-28 - Read directly from blockchain (source of truth)
    const fetchFromBlockchain = async (): Promise<BlockchainStats | null> => {
      const PLAYER_XP_ADDRESS = import.meta.env.VITE_PLAYER_XP_ADDRESS
      const RPC_URL = import.meta.env.VITE_BASE_RPC_URL || 'https://sepolia.base.org'
      
      if (!PLAYER_XP_ADDRESS) {
        console.warn('⚠️ VITE_PLAYER_XP_ADDRESS not set - cannot read from blockchain')
        return null
      }
      
      try {
        const provider = new ethers.JsonRpcProvider(RPC_URL)
        const playerXPContract = new ethers.Contract(PLAYER_XP_ADDRESS, PLAYER_XP_ABI, provider)
        
        console.log(`🔗 Reading player stats from blockchain for ${playerAddress.slice(0, 10)}...`)
        const data = await playerXPContract.getPlayerData(playerAddress)
        
        const totalBattles = Number(data[3])
        const wins = Number(data[4])
        const losses = Number(data[5])
        const draws = Number(data[6])
        
        const winRate = totalBattles > 0 ? Math.round((wins / totalBattles) * 100) : 0
        const lossRate = totalBattles > 0 ? Math.round((losses / totalBattles) * 100) : 0
        const drawRate = totalBattles > 0 ? Math.round((draws / totalBattles) * 100) : 0
        
        console.log(`✅ Blockchain data (direct): Level ${Number(data[1])}, Battles ${totalBattles}, Wins ${wins}, Losses ${losses}, Draws ${draws}`)
        
        return {
          totalExperience: Number(data[0]),
          level: Number(data[1]),
          lastBattleTimestamp: 0, // AGC: Contract returns experienceToNextLevel, not timestamp
          totalBattles,
          wins,
          losses,
          draws,
          winRate,
          lossRate,
          drawRate
        }
      } catch (blockchainError: any) {
        console.warn(`⚠️ Failed to read from blockchain: ${blockchainError.message}`)
        return null
      }
    }

    // AGC: Fallback to backend API if blockchain fails
    const fetchFromBackend = async (): Promise<BlockchainStats | null> => {
      const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001'
      
      try {
        const response = await fetch(`${backendUrl}/api/player-stats/${playerAddress}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
        
        if (!response.ok) {
          if (response.status === 404) {
            console.log(`📊 New player (no cached stats)`)
            return null
          }
          throw new Error(`HTTP ${response.status}`)
        }
        
        const data = await response.json()
        console.log(`✅ Backend stats (cached): Level ${data.level}, Battles ${data.totalBattles}`)
        
        return {
          totalExperience: data.totalExperience || 0,
          level: data.level || 1,
          lastBattleTimestamp: data.lastBattleTimestamp || 0,
          totalBattles: data.totalBattles || 0,
          wins: data.wins || 0,
          losses: data.losses || 0,
          draws: data.draws || 0,
          winRate: data.winRate || 0,
          lossRate: data.lossRate || 0,
          drawRate: data.drawRate || 0
        }
      } catch (err) {
        console.warn(`⚠️ Backend fallback failed: ${err}`)
        return null
      }
    }

    const fetchStats = async (source: 'initial' | 'event' | 'visibility' = 'initial') => {
      if (!playerAddress) {
        setStats(null)
        setLoading(false)
        return
      }

      // AGC: Prevent overlapping fetches when multiple events fire at once
      setLoading((prev) => (source === 'initial' ? true : prev))
      setError(null)

      try {
        console.log(`🔗 Fetching blockchain stats (${source}) for ${playerAddress.slice(0, 10)}...`)

        // AGC: FIX 2025-11-28 - Read directly from blockchain FIRST (source of truth)
        let blockchainData = await fetchFromBlockchain()
        
        // AGC: If blockchain query failed, try backend as fallback
        if (!blockchainData) {
          console.log(`📊 Trying backend API fallback...`)
          blockchainData = await fetchFromBackend()
        }

        if (!isUnmounted) {
          if (blockchainData) {
            setStats(blockchainData)
          } else {
            // AGC: No data from either source - use defaults
            console.log(`📊 No data available - using defaults`)
            setStats({
              totalExperience: 0,
              level: 1,
              lastBattleTimestamp: 0,
              totalBattles: 0,
              wins: 0,
              losses: 0,
              draws: 0,
              winRate: 0,
              lossRate: 0,
              drawRate: 0
            })
          }
        }
      } catch (err: any) {
        if (!isUnmounted) {
          console.error('❌ Error fetching blockchain stats:', err)
          setError(err.message)
        }
      } finally {
        if (!isUnmounted) {
          setLoading(false)
        }
      }
    }

    const handleHistoryEvent: EventListener = (event) => {
      const customEvent = event as CustomEvent<{ walletAddress?: string }>
      const updatedWallet = customEvent.detail?.walletAddress?.toLowerCase()
      if (!playerAddress || (updatedWallet && updatedWallet !== playerAddress.toLowerCase())) {
        return
      }
      fetchStats('event').catch((error) => {
        console.error('❌ Failed to refresh blockchain stats from event:', error)
      })
    }

    const handleVisibility = () => {
      if (!document.hidden) {
        fetchStats('visibility').catch((error) => {
          console.error('❌ Failed to refresh blockchain stats on visibility change:', error)
        })
      }
    }

    fetchStats('initial').catch((error) => {
      console.error('❌ Failed to load blockchain stats:', error)
    })

    window.addEventListener('origami:player-history-updated', handleHistoryEvent)
    window.addEventListener('origami:player-history-refresh', handleHistoryEvent)
    window.addEventListener('origami:blockchain-stats-refresh', handleHistoryEvent)
    window.addEventListener('visibilitychange', handleVisibility)

    return () => {
      isUnmounted = true
      window.removeEventListener('origami:player-history-updated', handleHistoryEvent)
      window.removeEventListener('origami:player-history-refresh', handleHistoryEvent)
      window.removeEventListener('origami:blockchain-stats-refresh', handleHistoryEvent)
      window.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [playerAddress])

  return { stats, loading, error }
}

